import { Component, Element, h, Host, Listen, Prop, State, Watch } from "@stencil/core";

import { debounce } from "../../utils/debounce";
import { getViewportWidth } from "../../utils/getViewportWidth";

/**
 * @slot (default) - The content of the accordion
 * @slot title - the element for the accordion title
 */
@Component({
    tag: "ux-accordion",
    styleUrl: "accordion.scss",
    shadow: false
})
export class Accordion {
    private baseClass = "ux-accordion";
    private contentContainer: HTMLDivElement;
    private titleContainer: HTMLButtonElement;
    private title: Element;
    private observer: MutationObserver;

    @Element() element!: HTMLUxAccordionElement;

    /** The amount in pixels from which the accordion functionality is deactivated */
    @Prop()
    readonly noAccordionFrom: number | null = null;
    /** The class to be passes to the title of the accordion when it becomes active */
    @Prop()
    readonly activeTitleClass: string = "ux-text-link--green";
    /** If a click event is detected on this selector, the accordion will not trigger */
    @Prop()
    readonly noClickSelector: string = "";
    /** Determines if accordion is opened on initial view */
    @Prop({
        mutable: true,
        reflect: true
    })
    isExpanded = false;

    @State() noAccordion = this.noAccordionFrom !== null ? window.innerWidth >= this.noAccordionFrom : false;
    @State() windowWidth: number = getViewportWidth();

    public connectedCallback() {
        window.addEventListener("resize", this.onResize);
    }

    componentDidLoad() {
        // @ts-expect-error Check for typing
        this.title = this.titleContainer.firstElementChild;
        this.element.querySelector("slot[name='title']")?.addEventListener("slotchange", () => {
            // @ts-expect-error Check for typing
            this.title = this.titleContainer.firstElementChild;
        });

        this.setIsExpanded();

        this.observer = new MutationObserver(() => this.handleSlotChange());
        this.observer.observe(this.contentContainer, { childList: true, subtree: true });

        const tabElement = this.element.closest("ux-tab");
        if (tabElement) {
            new MutationObserver(() => {
                if (tabElement.classList.contains("is-active")) {
                    requestAnimationFrame(() => this.setIsExpanded());
                }
            }).observe(tabElement, { attributes: true, attributeFilter: ["class"] });
        }
    }

    private onResize = debounce(() => {
        this.windowWidth = getViewportWidth();
        this.setIsExpanded();
        if (!this.noAccordionFrom) {
            return;
        }
        this.noAccordion = this.windowWidth >= this.noAccordionFrom;
        if (this.noAccordion && this.title !== null) {
            this.title.classList.remove(this.activeTitleClass);
        }
    }, 200);

    /**
     * Checks if the clicked element or its parentElement has any match with the given selector(tag, #id, .class)
     *
     * @param {string} selector - selector we are looking for
     * @param {object} clickedElement - the clicked event target
     * @return {boolean}
     */

    private matchingElement = (selector: string, clickedElement: HTMLElement) => {
        return clickedElement.closest(selector);
    };

    // eslint-disable-next-line @stencil-community/no-unused-watch
    @Watch("isExpanded")
    setIsExpanded() {
        this.setOrRemoveTitleClass();

        if (this.isExpanded) {
            this.contentContainer.style.maxHeight = this.contentContainer.scrollHeight + (this.windowWidth > 767 ? 65 : 50) + "px";
        } else {
            // @ts-expect-error Check for typing
            this.contentContainer.style.maxHeight = null;
        }
    }

    @Listen("uxCollapsibleExpanded", { target: "window" })
    handleCollapsibleExpansion() {
        this.onResize();
    }

    private handleSlotChange = () => {
        this.onResize();
    };

    private setOrRemoveTitleClass() {
        if (this.activeTitleClass !== "" && this.title !== null) {
            if (this.isExpanded) {
                this.title.classList.add(this.activeTitleClass);
            } else {
                this.title.classList.remove(this.activeTitleClass);
            }
        }
    }

    private handleAccordionClick = (e: Event) => {
        const clickTarget = e.target as HTMLElement;

        if (this.noClickSelector !== "") {
            if (this.matchingElement(this.noClickSelector, clickTarget)) {
                return;
            }
        }

        if (this.noAccordion) {
            return;
        }

        this.isExpanded = !this.isExpanded;
    };

    disconnectedCallback() {
        this.observer.disconnect();
        window.removeEventListener("resize", this.onResize);
    }

    render() {
        return (
            <Host
                class={{
                    [this.baseClass]: true,
                    [`${this.baseClass}--active`]: this.isExpanded && !this.noAccordion
                }}
            >
                <button
                    class={{
                        [`${this.baseClass}__button`]: true,
                        [`${this.baseClass}__title-container`]: !this.noAccordion
                    }}
                    onClick={(e) => this.handleAccordionClick(e)}
                    // @ts-expect-error Check for typing
                    ref={(el) => (this.titleContainer = el)}
                    aria-expanded={this.isExpanded ? "true" : "false"}
                    type="button"
                >
                    <slot name="title" />
                    {!this.noAccordion && <ux-icon name="chevron-down" weight="bold"></ux-icon>}
                </button>
                <div
                    role="region"
                    // @ts-expect-error Check for typing
                    ref={(el) => (this.contentContainer = el)}
                    class={{
                        [`${this.baseClass}__content-container`]: !this.noAccordion
                    }}
                    aria-hidden={!this.isExpanded && !this.noAccordion}
                >
                    <slot />
                </div>
            </Host>
        );
    }
}
